import {
  CollabType,
  type BulkCreateCollabsRequest,
  type CollabDeliveryMode,
  type CollabProposalEmailInput,
  type CollabRecipientInput,
} from '@fluenceur/types';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toaster } from '@/components/ui/toaster';
import { useInvitationActions, useInvitationData } from '@/contexts/invitation-context';
import { useBriefTemplateActions } from '@/hooks/actions/use-brief-template-actions';
import { useSendCollabsBulkAction } from '@/hooks/actions/use-send-collabs-bulk-action';
import { useInvalidateCollabList } from '@/hooks/queries/collab-keys';
import { knownApiErrorCode } from '@/libs/api-error';
import type { CollabSendResultLine } from '@/stores/invitation-store';
import { SEND_REFUSALS } from './collab-send-refusals';
import type { CollabWizardRecipient } from './use-collab-recipients';

/**
 * Orchestrates the wizard send: optionally persists a blank brief as a library
 * template first (the draft step's toggle), then creates-and-sends every
 * sendable recipient through the bulk endpoint (mono included: 1..N) with the
 * delivery mode explicitly picked on the channel step. Lines without identity
 * nor email are never sent — they surface as failed results so the done screen
 * reports them without blocking the batch.
 */
export const useCollabWizardSend = () => {
  const { t } = useTranslation();
  const {
    collabContent,
    collabName,
    collabBriefMode,
    collabTemplateId,
    respondByDays,
    saveAsTemplate,
    collabReproposedFromCollabId,
    collabBriefApplicationId,
  } = useInvitationData();
  const { setCollabSendResults, setCurrentView } = useInvitationActions();
  const { create: createTemplate, update: updateTemplate } = useBriefTemplateActions();
  const sendBulk = useSendCollabsBulkAction();
  const invalidateCollabList = useInvalidateCollabList();
  const [isSending, setIsSending] = useState(false);
  const sendingRef = useRef(false);

  const send = useCallback(
    async (
      recipients: CollabWizardRecipient[],
      mode: CollabDeliveryMode,
      proposalEmail?: CollabProposalEmailInput
    ) => {
      if (!collabContent || sendingRef.current) return;

      const sendable = recipients.filter((r) => r.creatorIdentityId !== null || r.email !== null);
      const unreachable = recipients.filter(
        (r) => r.creatorIdentityId === null && r.email === null
      );
      if (sendable.length === 0) return;

      sendingRef.current = true;
      setIsSending(true);
      try {
        // Blank brief with the "also save as template" toggle on: persist it to
        // the library first so the collab pins a template provenance.
        let briefTemplateId = collabTemplateId ?? undefined;
        if (collabBriefMode === 'blank' && saveAsTemplate) {
          const template = await createTemplate.mutateAsync({
            name: collabName,
            collabType: CollabType.SPONSORED_POST,
          });
          await updateTemplate.mutateAsync({ id: template.id, content: collabContent });
          briefTemplateId = template.id;
        }

        const inputs: CollabRecipientInput[] = sendable.map((r) =>
          r.creatorIdentityId ? { creatorIdentityId: r.creatorIdentityId } : { email: r.email! }
        );

        const results = await sendBulk.mutateAsync({
          recipients: inputs,
          name: collabName,
          briefTemplateId,
          content: collabContent,
          respondByDays,
          // Channel explicitly picked on the wizard's channel step.
          delivery: mode,
          // One common message for the whole batch, email mode only. Honored
          // server-side per recipient on the connected-mailbox channel; ignored
          // cleanly everywhere else.
          ...(mode === 'email' && proposalEmail ? { proposalEmail } : {}),
          // Sent only when this draft re-proposes an unfunded brief: the server
          // counts the one re-proposal it allows by that brief.
          ...(collabReproposedFromCollabId
            ? { reproposedFromCollabId: collabReproposedFromCollabId }
            : {}),
          // Sent only from the Candidatures page: the application this send answers.
          ...(collabBriefApplicationId ? { briefApplicationId: collabBriefApplicationId } : {}),
        } as BulkCreateCollabsRequest);

        // The backend processes recipients in order; map results back by index.
        const lines: CollabSendResultLine[] = sendable.map((recipient, index) => {
          const result = results[index];
          return {
            displayName: recipient.displayName,
            email: recipient.email,
            status: result?.status ?? 'failed',
            lookupKey: result?.lookupKey ?? null,
            code: result?.code ?? null,
            // The server's English text is never shown: the code is translated.
            message: null,
            // Delivery outcome is optional on the wire (backend lot 4): carry it
            // through when present, leave undefined otherwise.
            deliveryStatus: result?.deliveryStatus,
            deliveryErrorCode: result?.deliveryErrorCode ?? null,
            collabId: result?.collabId ?? null,
          };
        });
        for (const recipient of unreachable) {
          lines.push({
            displayName: recipient.displayName,
            email: null,
            status: 'failed',
            lookupKey: null,
            code: null,
            message: t('invitations.modal.collab.wizard.send.unreachableLine'),
          });
        }

        // The collabs exist now, so the tracking list is stale. Without this,
        // "Proposal created" is followed by an empty "My collabs" until a manual
        // reload — doubt right after the most important gesture of the product.
        // Called on any resolved batch, partial failures included: a mixed
        // result still wrote rows.
        invalidateCollabList();

        // A single recipient refused for something a step can fix is sent back
        // there, exactly as a refusal of the whole request would be: the bulk
        // route answers 200 and files the refusal on the line.
        const monoRefusal =
          lines.length === 1 && lines[0].status === 'failed' && lines[0].code
            ? SEND_REFUSALS[lines[0].code]
            : undefined;
        if (monoRefusal) {
          setCurrentView(monoRefusal.view);
          toaster.create({ type: 'error', title: t(monoRefusal.message) });
          return;
        }

        setCollabSendResults(lines);
        setCurrentView('collabDone');
      } catch (error) {
        // A refusal the brand can act on gets the screen that can act on it.
        // A toast alone would leave them on the delivery screen with no control
        // able to fix anything.
        const code = knownApiErrorCode(error);
        const refusal = code ? SEND_REFUSALS[code] : undefined;
        if (refusal) setCurrentView(refusal.view);
        toaster.create({
          type: 'error',
          title: t(refusal?.message ?? 'invitations.modal.collab.wizard.send.refusedError'),
        });
      } finally {
        sendingRef.current = false;
        setIsSending(false);
      }
    },
    [
      collabContent,
      collabTemplateId,
      collabBriefMode,
      saveAsTemplate,
      createTemplate,
      updateTemplate,
      collabName,
      collabReproposedFromCollabId,
      collabBriefApplicationId,
      sendBulk,
      invalidateCollabList,
      respondByDays,
      setCollabSendResults,
      setCurrentView,
      t,
    ]
  );

  return { send, isSending };
};
